<template>
  <teleport to="body">
    <transition
      name="modal-fade"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @leave="onLeave"
      @after-leave="onAfterLeave"
    >
      <div
        v-if="isVisible"
        class="makercart-modal-overlay"
        @click.self="closeModal"
      >
        <div class="makercart-modal-container container-xxl">
          <!-- Loading State -->
          <div v-if="loading" class="modal-loading-overlay">
            <div class="modal-loading-content">
              <div class="modal-loading-spinner"></div>
              <div class="modal-loading-text">Loading activity details...</div>
              <div class="modal-loading-subtitle">
                Please wait while we fetch the information
              </div>
            </div>
          </div>

          <!-- Main Modal Content -->
          <div v-else class="modal-main-content">
            <!-- Main Content -->
            <div class="makercart-modal-content-container">
              <!-- Header -->
              <div class="makercart-modal-header">
                <!-- Resource Views Banner -->
                <div v-if="!isAuthenticated" class="resource-views-banner">
                  <span class="banner-text">
                    You have
                    <span class="remaining-count">{{ limitResourceLeft }}</span
                    >/3 resource views remaining.
                    <span
                      ><button
                        type="button"
                        class="login-signup-btn"
                        @click="openLoginModal"
                      >
                        Sign In or Sign Up
                      </button></span
                    >

                    for free to unlock full access!
                  </span>
                </div>

                <div class="makercart-modal-title-container">
                  <h1 class="makercart-modal-title">
                    {{ makerCartData.makercart_activity || "Activity Title" }}
                  </h1>
                  <button
                    type="button"
                    class="mobile-makercart-modal-close"
                    aria-label="Close activity details"
                    @click="closeModal"
                  >
                    <CloseCircleIcon width="24" height="24" color="#3B4648" />
                  </button>
                </div>

                <button
                  type="button"
                  class="makercart-modal-close"
                  aria-label="Close activity details"
                  @click="closeModal"
                >
                  <CloseCircleIcon color="#3B4648" />
                </button>
              </div>
              <div class="makercart-modal-content-content">
                <!-- Left Section: Image Gallery -->
                <div class="makercart-gallery-section">
                  <div
                    class="main-image-container"
                    @click="openImageGalleryWithIndex(currentImageIndex)"
                  >
                    <img
                      :src="currentImage"
                      :alt="makerCartData.makercart_activity"
                      class="main-image"
                      @error="handleImageError"
                    />
                  </div>
                  <!-- Desktop Thumbnail Container -->
                  <div class="thumbnail-container" v-if="!isMobile">
                    <div
                      v-for="(image, index) in displayImages"
                      :key="index"
                      class="thumbnail-wrapper"
                      :class="{ active: index === currentImageIndex }"
                      @click="setCurrentImage(index)"
                    >
                      <img
                        :src="image"
                        :alt="`${makerCartData.makercart_activity} - Image ${index + 1}`"
                        class="thumbnail-image"
                        @error="handleImageError"
                      />
                    </div>
                    <button
                      v-if="hasMoreImages"
                      type="button"
                      class="view-more-btn"
                      @click="showImageGallery"
                    >
                      <span>View More</span>
                      <ArrowShortRightIcon
                        width="28"
                        height="28"
                        color="#026843"
                      />
                    </button>
                  </div>

                  <!-- Mobile Navigation Arrows -->
                  <div
                    v-if="isMobile && allImages.length > 1"
                    class="mobile-image-nav"
                  >
                    <button
                      type="button"
                      class="mobile-nav-btn mobile-nav-btn-left"
                      aria-label="Show previous image"
                      @click="previousImage"
                      :disabled="currentImageIndex === 0"
                    >
                      <ChevronLeftIcon width="16" height="16" color="#00925e" />
                    </button>
                    <button
                      type="button"
                      class="mobile-nav-btn mobile-nav-btn-right"
                      aria-label="Show next image"
                      @click="nextImage"
                      :disabled="currentImageIndex === allImages.length - 1"
                    >
                      <ChevronRightIcon
                        width="16"
                        height="16"
                        color="#00925e"
                      />
                    </button>
                  </div>
                </div>

                <!-- Center Section: Content -->
                <div class="makercart-content-section">
                  <!-- Badges -->
                  <div class="badges-container">
                    <CustomTooltip
                      placement="top"
                      text="Any content marked with the 'free resources' badge is available for download at no cost."
                      v-if="makerCartData.is_free"
                    >
                      <template #trigger>
                        <Badge variant="blue-light" label="Free" />
                      </template>
                    </CustomTooltip>
                    <Badge
                      v-if="makerCartData.popular"
                      variant="yellow-light"
                      label="Popular"
                    />
                  </div>

                  <!-- Tags -->
                  <div class="tags-container">
                    <div
                      v-for="(tag, idx) in visibleTags"
                      :key="`${tag}-${idx}`"
                      class="tag-button"
                    >
                      {{ tag }}
                    </div>
                    <div
                      v-if="remainingTagsCount > 0 || showAllTags"
                      class="tag-button tag-button-more clickable"
                      :class="
                        showAllTags ? 'tag-button-less' : 'tag-button-more'
                      "
                      @click="toggleAllTags"
                      @keydown.enter="toggleAllTags"
                      @keydown.space.prevent="toggleAllTags"
                      role="button"
                      tabindex="0"
                    >
                      {{
                        showAllTags
                          ? "View Less"
                          : `+ ${remainingTagsCount} more`
                      }}
                    </div>
                  </div>

                  <!-- Divider -->
                  <div class="divider"></div>

                  <!-- Overview and Lesson Outcomes -->
                  <div class="content-sections">
                    <div class="content-column">
                      <h3 class="section-title">Overview</h3>
                      <div
                        class="section-content formatted-description"
                        v-html="
                          formatAirtableText(
                            makerCartData.activity_description ||
                              'No description available.'
                          )
                        "
                      ></div>
                    </div>
                    <div
                      class="content-column"
                      v-if="makerCartData.learning_outcomes"
                    >
                      <h3 class="section-title">Lesson Outcomes</h3>
                      <div class="section-content">
                        <div
                          class="formatted-outcomes"
                          v-html="
                            formatLearningOutcomes(
                              makerCartData.learning_outcomes
                            )
                          "
                        ></div>
                      </div>
                    </div>
                  </div>

                  <!-- Lesson Materials Button -->
                  <CustomTooltip
                    placement="top"
                    :text="makerCartData.note || ''"
                    v-if="
                      (makerCartData.is_disabled ||
                        !makerCartData.public_visibility) &&
                      makerCartData.note
                    "
                  >
                    <template #trigger>
                      <button
                        type="button"
                        class="sample-materials-btn"
                        :disabled="
                          makerCartData.is_disabled ||
                          !makerCartData.public_visibility
                        "
                        @click="viewLessonMaterials"
                      >
                        <ExternalLinkIcon width="18" height="18" />
                        Lesson Materials
                      </button>
                    </template>
                  </CustomTooltip>
                  <button
                    v-else
                    type="button"
                    class="sample-materials-btn"
                    :disabled="
                      makerCartData.is_disabled ||
                      !makerCartData.public_visibility
                    "
                    @click="viewLessonMaterials"
                  >
                    <ExternalLinkIcon width="18" height="18" />
                    Lesson Materials
                  </button>

                  <!-- Divider -->
                  <div class="divider"></div>

                  <!-- Expandable Details Section -->
                  <div class="details-section">
                    <button
                      type="button"
                      class="details-toggle"
                      @click="toggleDetails"
                      :class="{ expanded: showDetails }"
                    >
                      <span>{{
                        showDetails ? "Hide Details" : "View Details"
                      }}</span>
                      <ChevronDownIcon width="20" height="20" />
                    </button>

                    <transition name="details-expand">
                      <div v-if="showDetails" class="details-content">
                        <div class="details-grid">
                          <!-- Materials Provided -->
                          <div class="detail-column">
                            <div class="detail-title-with-icon">
                              <CustomTooltip
                                placement="top"
                                text="Each cart is packed with enough materials to run your chosen activities—scaled to your selected student count."
                              >
                                <template #trigger>
                                  <img
                                    src="/images/icon/i-circle-icon.svg"
                                    style="margin-bottom: 8px"
                                    alt=""
                                  />
                                </template>
                              </CustomTooltip>
                              <h4 class="detail-title">Materials provided</h4>
                            </div>
                            <ul class="detail-list">
                              <li
                                v-for="material in materialsProvided"
                                :key="material"
                              >
                                {{ material }}
                              </li>
                            </ul>
                          </div>

                          <!-- You'll need to provide -->
                          <div class="detail-column border-column">
                            <div class="detail-title-with-icon">
                              <CustomTooltip
                                placement="top"
                                text="These items are not provided as part of the Makercart order."
                              >
                                <template #trigger>
                                  <img
                                    src="/images/icon/i-circle-icon.svg"
                                    style="margin-bottom: 8px"
                                    alt=""
                                  />
                                </template>
                              </CustomTooltip>
                              <h4 class="detail-title">
                                You'll need to provide
                              </h4>
                            </div>
                            <ul class="detail-list">
                              <li
                                v-for="material in materialsToProvide"
                                :key="material"
                              >
                                {{ material }}
                              </li>
                              <li v-if="materialsToProvide.length === 0">
                                No additional materials needed for this activity
                              </li>
                            </ul>
                          </div>

                          <!-- Required Equipment -->
                          <div class="detail-column border-column">
                            <h4 class="detail-title">Required Equipment</h4>
                            <ul class="detail-list">
                              <li
                                v-for="equipment in makerCartData.hardware_associated"
                                :key="equipment"
                              >
                                {{ equipment }}
                              </li>
                            </ul>
                          </div>
                        </div>
                        <!-- Standards -->
                        <div class="detail-column detail-column-wide">
                          <h4 class="detail-title">Standards</h4>
                          <div class="detail-list">
                            {{ makerCartData.standards || "N/A" }}
                          </div>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mobile Footer (Sticky) -->
            <div v-if="isMobile" class="mobile-modal-footer">
              <button
                type="button"
                class="mobile-add-to-cart-btn"
                @click="toggleMobileSidebar"
              >
                <ShoppingCartIcon width="20" height="20" color="#ffff" />
                Add to Cart
              </button>
            </div>

            <!-- Right Section: Selection Sidebar -->
            <div class="makercart-sidebar">
              <div class="sidebar-content">
                <h3 class="sidebar-title">Select Options</h3>
                <div class="sidebar-divider"></div>

                <!-- Number of Students -->
                <div :class="locale == 'US' ? 'form-group' : ''">
                  <div class="form-label-with-icon">
                    <CustomTooltip
                      placement="top"
                      text="We'll pack materials based on the selected number of students. You can increase the number of students by multiples of 10"
                      v-if="locale == 'US'"
                    >
                      <template #trigger>
                        <img
                          src="/images/icon/i-circle-icon.svg"
                          style="margin-bottom: 4px"
                          alt=""
                        />
                      </template>
                    </CustomTooltip>
                    <label class="form-label">Number of projects</label>
                  </div>
                  <div class="number-input">
                    <button
                      type="button"
                      class="number-btn"
                      aria-label="Decrease number of projects"
                      @click="decrementStudents"
                      :disabled="selectedStudents <= 80 || locale === 'SG'"
                    >
                      <MinusIcon width="16" height="16" />
                    </button>
                    <input
                      type="number"
                      role="spinbutton"
                      class="number-input-field"
                      v-model.number="projectInput"
                      min="80"
                      max="150"
                      step="10"
                      inputmode="numeric"
                      aria-label="Number of projects"
                      :aria-valuenow="projectInput"
                      aria-valuemin="80"
                      aria-valuemax="150"
                      @input="handleProjectsInput"
                      @blur="snapProjects"
                      :disabled="locale === 'SG'"
                    />
                    <button
                      type="button"
                      class="number-btn"
                      aria-label="Increase number of projects"
                      @click="incrementStudents"
                      :disabled="selectedStudents >= 150 || locale === 'SG'"
                    >
                      <PlusIcon width="16" height="16" />
                    </button>
                  </div>
                </div>

                <div class="form-group" v-if="locale !== 'SG'">
                  <div class="form-label-with-icon">
                    <CustomTooltip
                      placement="top"
                      text="Add a full-color 24” × 36” poster to any Makercart activity for $50. Perfect for self-guided or drop-in sessions, these posters keep key steps and ideas front and center so students can explore independently and stay inspired."
                    >
                      <template #trigger>
                        <img
                          src="/images/icon/i-circle-icon.svg"
                          style="margin-bottom: 4px"
                          alt=""
                        />
                      </template>
                    </CustomTooltip>
                    <label class="form-label">Make it Visual</label>
                  </div>
                  <div class="checkbox-container">
                    <CustomCheckbox
                      v-model="selectedPoster"
                      label="Add Poster"
                    />
                  </div>
                </div>

                <!-- Grade Selection -->
                <div class="form-group" v-if="locale !== 'SG'">
                  <div class="form-label-with-icon">
                    <CustomTooltip
                      placement="top"
                      text="We'll customize your lesson materials based on grade"
                    >
                      <template #trigger>
                        <img
                          src="/images/icon/i-circle-icon.svg"
                          style="margin-bottom: 4px"
                          alt=""
                        />
                      </template>
                    </CustomTooltip>
                    <label class="form-label">Choose Grade</label>
                  </div>
                  <div class="radio-group">
                    <label
                      v-for="grade in availableGrades"
                      :key="grade"
                      class="radio-option"
                    >
                      <input
                        type="radio"
                        :value="grade"
                        v-model="selectedGrade"
                        class="radio-input"
                      />
                      <span class="radio-custom"></span>
                      <span class="radio-label">{{ grade }}</span>
                    </label>
                  </div>
                </div>

                <!-- Focus Selection -->
                <div class="" v-if="locale === 'US'">
                  <div class="form-label-with-icon">
                    <CustomTooltip
                      placement="top"
                      text="Focus sets the lesson's orientation: Sustainability highlights climate education, Engineering Design emphasizes the design process, and Classic Maker centers on hands-on maker education"
                    >
                      <template #trigger>
                        <img
                          src="/images/icon/i-circle-icon.svg"
                          style="margin-bottom: 4px"
                          alt=""
                        />
                      </template>
                    </CustomTooltip>
                    <label class="form-label">Choose Focus</label>
                  </div>
                  <div class="radio-group">
                    <label
                      v-for="focus in availableFocus"
                      :key="focus"
                      class="radio-option"
                      :class="{ disabled: !isFocusEnabled(focus) }"
                    >
                      <input
                        type="radio"
                        :value="focus"
                        v-model="selectedFocus"
                        :disabled="!isFocusEnabled(focus)"
                        class="radio-input"
                      />
                      <span class="radio-custom"></span>
                      <span class="radio-label">{{ focus }}</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="sidebar-actions">
                <div class="action-divider"></div>
                <div class="action-buttons">
                  <template v-if="!props.editMode">
                    <v-menu z-index="10000000" v-if="locale !== 'SG'">
                      <template #activator="{ props: menuProps }">
                        <ActionButton
                          variant="primary"
                          :disabled="!canAddToCart"
                          :loading="isCartLoading"
                          full-width
                          v-bind="menuProps"
                        >
                          Add to Cart
                          <template #icon-right>
                            <ShoppingCartIcon
                              width="20"
                              height="20"
                              color="#ffff"
                            />
                          </template>
                        </ActionButton>
                      </template>
                      <v-list>
                        <v-list-item
                          v-for="(item, index) in availableCarts"
                          :key="index"
                          :value="item.value"
                          @click="handleCartSelection(item.value)"
                        >
                          <v-list-item-title>{{ item.text }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>

                    <ActionButton
                      v-else
                      variant="primary"
                      :disabled="!canAddToCart"
                      :loading="isCartLoading"
                      full-width
                      @click="handleSgAddToCart"
                    >
                      Add to Cart
                      <template #icon-right>
                        <ShoppingCartIcon
                          width="20"
                          height="20"
                          color="#ffff"
                        />
                      </template>
                    </ActionButton>

                    <ActionButton
                      v-if="locale !== 'SG'"
                      variant="secondary"
                      full-width
                      :disabled="!canAddSingleActivity"
                      :loading="isCartLoading"
                      @click="handleAddSingleActivity"
                    >
                      <template #icon-right>
                        <StackIcon width="19" height="20" />
                      </template>
                      Add Single Activity
                    </ActionButton>

                    <ActionButton
                      variant="ghost"
                      @click="shareActivity"
                      full-width
                    >
                      <template #icon-left>
                        <ShareIcon width="20" height="20" />
                      </template>
                      Share
                    </ActionButton>
                  </template>

                  <template v-else>
                    <ActionButton
                      variant="primary"
                      full-width
                      :disabled="!canSaveChanges"
                      :loading="isCartLoading"
                      @click="handleSaveChanges"
                    >
                      Save Changes
                    </ActionButton>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile Bottom Sheet -->
        <div
          v-if="isMobile && showMobileSidebar"
          class="mobile-sidebar-backdrop"
          @click="closeMobileSidebar"
        ></div>

        <div
          v-if="isMobile"
          class="mobile-bottom-sheet"
          :class="{ show: showMobileSidebar }"
        >
          <div class="mobile-bottom-sheet-content">
            <div class="mobile-bottom-sheet-header">
              <h3 class="mobile-bottom-sheet-title">Select Options</h3>
              <button
                type="button"
                class="mobile-bottom-sheet-close"
                aria-label="Close options sheet"
                @click="closeMobileSidebar"
              >
                <img src="/images/icon/x-circle-icon.svg" alt="Close" />
              </button>
            </div>

            <div class="mobile-bottom-sheet-body">
              <!-- Number of Students -->
              <div class="mobile-form-group">
                <div class="mobile-form-label-with-icon">
                  <CustomTooltip
                    placement="top"
                    text="We'll pack materials based on the selected number of students. You can increase the number of students by multiples of 10"
                  >
                    <template #trigger>
                      <img
                        src="/images/icon/i-circle-icon.svg"
                        style="margin-bottom: 4px"
                        alt=""
                      />
                    </template>
                  </CustomTooltip>
                  <label class="mobile-form-label">Number of projects</label>
                </div>
                <div class="mobile-number-input">
                  <button
                    type="button"
                    class="mobile-number-btn"
                    aria-label="Decrease number of projects"
                    @click="decrementStudents"
                    :disabled="selectedStudents <= 80 || locale === 'SG'"
                  >
                    <MinusIcon width="16" height="16" />
                  </button>
                  <input
                    type="number"
                    role="spinbutton"
                    class="mobile-number-input-field"
                    v-model.number="projectInput"
                    min="80"
                    max="150"
                    step="10"
                    inputmode="numeric"
                    aria-label="Number of projects"
                    :aria-valuenow="projectInput"
                    aria-valuemin="80"
                    aria-valuemax="150"
                    @input="handleProjectsInput"
                    @blur="snapProjects"
                    :disabled="locale === 'SG'"
                  />
                  <button
                    type="button"
                    class="mobile-number-btn"
                    aria-label="Increase number of projects"
                    @click="incrementStudents"
                    :disabled="selectedStudents >= 150 || locale === 'SG'"
                  >
                    <PlusIcon width="16" height="16" />
                  </button>
                </div>
                <div class="mobile-form-group" v-if="locale !== 'SG'">
                  <div class="form-label-with-icon">
                    <CustomTooltip
                      placement="top"
                      text="Add a full-color 24” × 36” poster to any Makercart activity for $50. Perfect for self-guided or drop-in sessions, these posters keep key steps and ideas front and center so students can explore independently and stay inspired."
                    >
                      <template #trigger>
                        <img
                          src="/images/icon/i-circle-icon.svg"
                          style="margin-bottom: 4px"
                          alt=""
                        />
                      </template>
                    </CustomTooltip>
                    <label class="form-label">Make it Visual</label>
                  </div>
                  <div class="mobile-checkbox-container">
                    <CustomCheckbox
                      v-model="selectedPoster"
                      label="Add Poster"
                    />
                  </div>
                </div>
              </div>

              <!-- Grade Selection -->
              <div class="mobile-form-group" v-if="locale !== 'SG'">
                <div class="mobile-form-label-with-icon">
                  <CustomTooltip
                    placement="top"
                    text="We'll customize your lesson materials based on grade"
                  >
                    <template #trigger>
                      <img
                        src="/images/icon/i-circle-icon.svg"
                        style="margin-bottom: 4px"
                        alt=""
                      />
                    </template>
                  </CustomTooltip>
                  <label class="mobile-form-label">Choose Grade</label>
                </div>
                <div class="mobile-radio-group">
                  <label
                    v-for="grade in availableGrades"
                    :key="grade"
                    class="mobile-radio-option"
                  >
                    <input
                      type="radio"
                      :value="grade"
                      v-model="selectedGrade"
                      class="mobile-radio-input"
                    />
                    <span class="mobile-radio-custom"></span>
                    <span class="mobile-radio-label">{{ grade }}</span>
                  </label>
                </div>
              </div>

              <!-- Focus Selection -->
              <div class="mobile-form-group" v-if="locale === 'US'">
                <div class="mobile-form-label-with-icon">
                  <CustomTooltip
                    placement="top"
                    text="Focus sets the lesson's orientation: Sustainability highlights climate education, Engineering Design emphasizes the design process, and Classic Maker centers on hands-on maker education"
                  >
                    <template #trigger>
                      <img
                        src="/images/icon/i-circle-icon.svg"
                        style="margin-bottom: 4px"
                        alt=""
                      />
                    </template>
                  </CustomTooltip>
                  <label class="mobile-form-label">Choose Focus</label>
                </div>
                <div class="mobile-radio-group">
                  <label
                    v-for="focus in availableFocus"
                    :key="focus"
                    class="mobile-radio-option"
                    :class="{ disabled: !isFocusEnabled(focus) }"
                  >
                    <input
                      type="radio"
                      :value="focus"
                      v-model="selectedFocus"
                      :disabled="!isFocusEnabled(focus)"
                      class="mobile-radio-input"
                    />
                    <span class="mobile-radio-custom"></span>
                    <span class="mobile-radio-label">{{ focus }}</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Mobile Action Buttons -->
            <div class="mobile-bottom-sheet-actions">
              <div class="mobile-action-divider"></div>
              <div class="mobile-action-buttons">
                <template v-if="!props.editMode">
                  <v-menu z-index="10000000" v-if="locale !== 'SG'">
                    <template #activator="{ props: menuProps }">
                      <ActionButton
                        variant="primary"
                        :disabled="!canAddToCart"
                        :loading="isCartLoading"
                        full-width
                        v-bind="menuProps"
                      >
                        <template #icon-left>
                          <ShoppingCartIcon
                            width="20"
                            height="20"
                            color="#ffff"
                          />
                        </template>
                        Add to Cart
                      </ActionButton>
                    </template>
                    <v-list>
                      <v-list-item
                        v-for="(item, index) in availableCarts"
                        :key="index"
                        :value="item.value"
                        @click="handleCartSelection(item.value)"
                      >
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>

                  <ActionButton
                    v-else
                    variant="primary"
                    :disabled="!canAddToCart"
                    :loading="isCartLoading"
                    full-width
                    @click="handleSgAddToCart"
                  >
                    <template #icon-left>
                      <ShoppingCartIcon width="20" height="20" color="#ffff" />
                    </template>
                    Add to Cart
                  </ActionButton>

                  <ActionButton
                    variant="secondary"
                    full-width
                    :disabled="!canAddSingleActivity"
                    :loading="isCartLoading"
                    @click="handleAddSingleActivity"
                    v-if="locale !== 'SG'"
                  >
                    <template #icon-right>
                      <StackIcon width="19" height="20" />
                    </template>
                    Add Single Activity
                  </ActionButton>

                  <ActionButton
                    variant="ghost"
                    @click="shareActivity"
                    full-width
                  >
                    <template #icon-left>
                      <ShareIcon width="20" height="20" />
                    </template>
                    Share
                  </ActionButton>
                </template>

                <template v-else>
                  <ActionButton
                    variant="primary"
                    full-width
                    :disabled="!canSaveChanges"
                    :loading="isCartLoading"
                    @click="handleSaveChanges"
                  >
                    Save Changes
                  </ActionButton>
                </template>
              </div>
            </div>
          </div>
        </div>

        <ChatbotAssistant
          :position="isMobile ? 'right' : 'left'"
          page-name="Activity Card"
          :activity-name="makerCartData.makercart_activity"
        />
      </div>
    </transition>

    <!-- Image Gallery Modal -->
    <ImageGalleryModal
      ref="galleryModalRef"
      :visible="showImageGalleryModal"
      :images="allImages"
      :initial-index="galleryInitialIndex"
      @close="closeImageGallery"
    />

    <AuthModal
      :is-open="showAuthModal"
      initial-view="login"
      @close="handleCloseAuthModal"
      @login-success="handleLoginSuccess"
      :z-index="100000000"
    />
  </teleport>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  inject,
  defineExpose,
  defineEmits,
  defineProps,
} from "vue";
import CustomTooltip from "@/components/common/CustomTooltip.vue";
import ImageGalleryModal from "@/components/maker-cart/ImageGalleryModal.vue";
import AuthModal from "@/components/modal/AuthModal.vue";
import { useCart } from "@/composeables/useCart";
import cookiesService from "@/services/cookiesService";
import makercartActivityService from "@/services/makercartActivityService";
import StemStudioService from "@/services/stemStudioService";
import { LOCALE_STORAGE_KEY } from "@/utils/constants";
import ActionButton from "@/components/common/ActionButton.vue";
import Badge from "@/components/common/Badge.vue";
import CustomCheckbox from "@/components/common/CustomCheckbox.vue";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  ChevronDownIcon,
  MinusIcon,
  PlusIcon,
  ShoppingCartIcon,
  StackIcon,
  ShareIcon,
  ArrowRightIcon,
  CloseCircleIcon,
} from "@/components/icons";
import ChatbotAssistant from "@/components/maker-cart/ChatbotAssistant.vue";
import { useMakercartTags, formatAirtableText } from "../useMakerCart";
import { useLocaleStore } from "@/store/locale";
import ArrowShortRightIcon from "@/components/icons/ArrowShortRightIcon.vue";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  makercartId: {
    type: [String, Number],
    default: null,
  },
  editMode: {
    type: Boolean,
    default: false,
  },
  cartId: {
    type: [String, Number],
    default: null,
  },
  activityData: {
    type: Object,
    default: null,
  },
  cartType: {
    default: "custom",
  },
});

const emit = defineEmits(["close", "add-to-cart", "saved"]);

const localeStore = useLocaleStore();
const locale = computed(() => localeStore.currentLocale);
const SGStudentsDefaultCount = 60;

// Composables
const {
  availableCarts,
  handleMakercartSelection,
  addSingleActivity,
  updateActivityInMakercart,
  isCartLoading,
  hasFullActivityCart,
  makercarts,
} = useCart();
const notify = inject("notify");

// Reactive data
const isVisible = ref(false);
const makerCartData = ref({});
const loading = ref(false);
const showDetails = ref(false);
const currentImageIndex = ref(0);
const selectedStudents = ref(
  locale.value === "SG" ? SGStudentsDefaultCount : 80
);
const projectInput = ref(locale.value === "SG" ? SGStudentsDefaultCount : 80);
let snapTimer = null;
const selectedGrade = ref("");
const selectedFocus = ref("");
const selectedPoster = ref(false);

// Image Gallery Modal State
const showImageGalleryModal = ref(false);
const galleryModalRef = ref(null);
const galleryInitialIndex = ref(0);

// Materials data (will be populated from API)
const materialsProvided = ref([]);
const materialsToProvide = ref([]);

const isAuthenticated = ref(false);
const limitResourceLeft = ref(3);
const viewedActivityIds = ref([]);
const showAuthModal = ref(false);
const isMobile = ref(false);
const showMobileSidebar = ref(false);

// Use tags composable
const {
  showAllTags,
  visibleTags,
  remainingTagsCount,
  toggleAllTags,
  resetTags,
} = useMakercartTags(makerCartData, isMobile);

// Computed properties
const currentImage = computed(() => {
  const images = makerCartData.value.image_thumbnail || [];
  return images[currentImageIndex.value] || "/images/placeholder.jpg";
});

const displayImages = computed(() => {
  const images = makerCartData.value.image_thumbnail || [];
  return images.slice(0, 2); // Show first 2 images as thumbnails
});

const allImages = computed(() => {
  return makerCartData.value.image_thumbnail || [];
});

const hasMoreImages = computed(() => {
  const images = makerCartData.value.image_thumbnail || [];
  return images.length > 2;
});

const openLoginModal = () => {
  showAuthModal.value = true;
};
const handleCloseAuthModal = () => {
  showAuthModal.value = false;
};
const handleLoginSuccess = () => {
  isAuthenticated.value = true;
  showAuthModal.value = false;
  // (Optional) Re-fetch limits if you want to refresh the counter post-login
  fetchLimitCounter();
};

const checkAuth = async () => {
  const token = await cookiesService.getCookie("authToken");
  isAuthenticated.value = !!token;
};

// Get remaining resource views & already viewed IDs
const fetchLimitCounter = async () => {
  try {
    const res = await makercartActivityService.getLimitCounter();
    if (res?.status === 200) {
      limitResourceLeft.value = 3 - (res.data?.data?.count ?? 0);
      viewedActivityIds.value = res.data?.data?.activity_ids ?? [];
    }
  } catch (e) {
    // Fail silently; banner still renders with default
    console.warn("Failed to fetch limit counter", e);
  }
};

const availableGrades = computed(() => {
  const grades = makerCartData.value.grade_level || [];
  return grades.length > 0 ? grades : ["Lower Elementary", "Upper Elementary"];
});

const availableFocus = computed(() => {
  const focus = makerCartData.value.focus || [];
  return focus.length > 0
    ? focus
    : ["Sustainability", "Engineering Design", "Classic Maker"];
});

const canAddToCart = computed(() => {
  const isGradeValid = locale.value === "SG" ? true : !!selectedGrade.value;
  const isFocusValid = locale.value === "US" ? !!selectedFocus.value : true;
  return isGradeValid && isFocusValid;
});

const canAddSingleActivity = computed(() => {
  const isGradeValid = locale.value === "SG" ? true : !!selectedGrade.value;
  const isFocusValid = locale.value === "US" ? !!selectedFocus.value : true;
  return isGradeValid && isFocusValid;
});

const canSaveChanges = computed(() => {
  const isGradeValid = locale.value === "SG" ? true : !!selectedGrade.value;
  const isFocusValid = locale.value === "US" ? !!selectedFocus.value : true;
  return isGradeValid && isFocusValid;
});

// Methods
const fetchMakerCartData = async (id) => {
  if (!id) return;

  try {
    loading.value = true;
    const localeToUse = localStorage.getItem(LOCALE_STORAGE_KEY) || "US";
    const response = await StemStudioService.getById(id, localeToUse);

    if (response.status === 200) {
      makerCartData.value = response.data.data;

      // Fetch materials data
      if (makerCartData.value.material_needed?.length) {
        const materialNeededString =
          makerCartData.value.material_needed.join(",");
        const materialData = await getMakerCartByIds(materialNeededString);

        materialsProvided.value = [];
        materialsToProvide.value = [];

        if (materialData) {
          materialData.forEach((item) => {
            if (item.includedInMC) {
              materialsProvided.value.push(item.name);
            } else {
              materialsToProvide.value.push(item.name);
            }
          });
        }
      }

      // Set selections for edit or default
      if (props.editMode && props.activityData) {
        selectedGrade.value =
          props.activityData.grade || availableGrades.value[0] || "";
        selectedFocus.value =
          props.activityData.focus || availableFocus.value[0] || "";
        selectedStudents.value =
          typeof props.activityData.projects === "number"
            ? props.activityData.projects
            : locale.value === "SG"
              ? SGStudentsDefaultCount
              : 80;
        projectInput.value = selectedStudents.value;
        selectedPoster.value = !!props.activityData.includingPoster;
      } else {
        selectedStudents.value =
          locale.value === "SG" ? SGStudentsDefaultCount : 80;
        projectInput.value = selectedStudents.value;
        if (availableGrades.value.length > 0) {
          selectedGrade.value = availableGrades.value[0];
        }
        if (availableFocus.value.length > 0) {
          selectedFocus.value = availableFocus.value[0];
        }
      }
    }
  } catch (error) {
    console.error("Error fetching makercart data:", error);
    notify?.error("Failed to load activity details");
  } finally {
    loading.value = false;
  }
};

const handleSaveChanges = async () => {
  try {
    if (!props.editMode || !props.cartId || !props.activityData) return;
    const updates = {
      grade: locale.value === "SG" ? "" : selectedGrade.value,
      focus: locale.value === "SG" ? "" : selectedFocus.value,
      projects: selectedStudents.value,
      includingPoster: locale.value === "SG" ? false : selectedPoster.value,
      makercart_id: props.makercartId,
    };

    const result = await updateActivityInMakercart(
      props.cartId,
      props.activityData.entry_id,
      updates,
      props.cartType
    );

    if (result.success) {
      notify?.plain("Changes saved");
      emit("saved");
      closeModal();
    } else {
      notify?.error(result.error || "Failed to save changes");
    }
  } catch (error) {
    console.error("Error saving changes:", error);
    notify?.error("Failed to save changes");
  }
};

const getMakerCartByIds = async (ids) => {
  try {
    const response = await StemStudioService.getMaterialByIdBulk(ids);
    if (response.status === 200) {
      return response.data.data;
    }
  } catch (error) {
    console.error("Error fetching materials:", error);
  }
  return [];
};

const setCurrentImage = (index) => {
  currentImageIndex.value = index;
};

const previousImage = () => {
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--;
  }
};

const nextImage = () => {
  if (currentImageIndex.value < allImages.value.length - 1) {
    currentImageIndex.value++;
  }
};

const showImageGallery = () => {
  galleryInitialIndex.value = 0;
  showImageGalleryModal.value = true;
};

const openImageGalleryWithIndex = (index) => {
  galleryInitialIndex.value = index || 0;
  showImageGalleryModal.value = true;
};

const closeImageGallery = () => {
  showImageGalleryModal.value = false;
};

const handleImageError = (event) => {
  event.target.src = "/images/placeholder.jpg";
};

const toggleDetails = () => {
  showDetails.value = !showDetails.value;
};

const incrementStudents = () => {
  if (locale.value === "SG") return;
  if (selectedStudents.value < 150) {
    selectedStudents.value = Math.min(selectedStudents.value + 10, 150);
    projectInput.value = selectedStudents.value;
  }
};

const decrementStudents = () => {
  if (locale.value === "SG") return;
  if (selectedStudents.value > 80) {
    selectedStudents.value = Math.max(selectedStudents.value - 10, 80);
    projectInput.value = selectedStudents.value;
  }
};

function handleProjectsInput() {
  if (locale.value === "SG") return;
  if (snapTimer) clearTimeout(snapTimer);
  snapTimer = setTimeout(() => {
    snapProjects();
  }, 400);
}

function snapProjects() {
  if (locale.value === "SG") {
    projectInput.value = SGStudentsDefaultCount;
    selectedStudents.value = SGStudentsDefaultCount;
    return;
  }
  let val = Number(projectInput.value);
  if (Number.isNaN(val)) {
    val = 80;
  }
  // Round to nearest multiple of 10
  val = Math.round(val / 10) * 10;
  // Clamp to allowed range
  val = Math.min(150, Math.max(80, val));
  projectInput.value = val;
  selectedStudents.value = val;
}

const isFocusEnabled = (focus) => {
  const availableFocusOptions = makerCartData.value.focus || [];
  return (
    availableFocusOptions.length === 0 || availableFocusOptions.includes(focus)
  );
};

const handleCartSelection = async (cartValue) => {
  try {
    const activityData = {
      makercart_id: makerCartData.value.makercart_id,
      title: makerCartData.value.makercart_activity,
      activity_description: makerCartData.value.activity_description,
      image_thumbnail: makerCartData.value.image_thumbnail,
      grade: locale.value === "SG" ? "" : selectedGrade.value,
      focus: locale.value === "SG" ? "" : selectedFocus.value,
      projects: selectedStudents.value,
      includingPoster: locale.value === "SG" ? false : selectedPoster.value,
      addedAt: new Date().toISOString(),
      is_free: makerCartData.value.is_free,
      popular: makerCartData.value.popular,
    };

    const result = await handleMakercartSelection(cartValue, activityData);

    if (result.success) {
      emit("add-to-cart", {
        cartId: result.cart.id,
        activity: activityData,
      });

      notify?.plain("Successfully added to cart!");
      closeModal();
    } else {
      notify?.error(result.error || "Failed to add to cart");
    }
  } catch (error) {
    console.error("Error adding to cart:", error);
    notify?.error("Failed to add to cart");
  }
};

const handleSgAddToCart = async () => {
  const targetCartValue =
    makercarts.value.length > 0 ? makercarts.value[0].id : "create_new";
  await handleCartSelection(targetCartValue);
};

const handleAddSingleActivity = async () => {
  try {
    const activityData = {
      makercart_id: makerCartData.value.makercart_id,
      makercart_activity: makerCartData.value.makercart_activity,
      activity_description: makerCartData.value.activity_description,
      image_thumbnail: makerCartData.value.image_thumbnail,
      grade: selectedGrade.value,
      focus: selectedFocus.value,
      projects: selectedStudents.value,
      includingPoster: selectedPoster.value,
      addedAt: new Date().toISOString(),
      is_free: makerCartData.value.is_free,
      popular: makerCartData.value.popular,
    };

    const result = await addSingleActivity(activityData);
    if (result.success) {
      notify?.plain("Successfully added activity");
      closeModal();
    } else {
      notify?.error(result.error || "Failed to add activity");
    }
  } catch (error) {
    console.error("Error adding single activity:", error);
    notify?.error("Failed to add activity");
  }
};

const shareActivity = () => {
  if (navigator.share) {
    navigator.share({
      title: makerCartData.value.makercart_activity,
      text: makerCartData.value.activity_description,
      url: window.location.href,
    });
  } else {
    // Fallback: copy URL to clipboard
    navigator.clipboard.writeText(window.location.href);
    notify?.plain("Link copied to clipboard!");
  }
};

const formatLearningOutcomes = (text) => {
  if (!text) return "";

  // Replace newlines with <br> tags and handle numbered lists
  let formattedText = text.replace(/\n/g, "<br>").replace(/\\n/g, "<br>"); // Handle escaped newlines

  // If text contains numbered items (like "1.", "2.", etc.), wrap them in proper list structure
  const hasNumberedList = /\d+\.\s/.test(formattedText);

  if (hasNumberedList) {
    // Split by line breaks and process each line
    const lines = formattedText.split("<br>");
    let result = "";
    let inList = false;

    lines.forEach((line) => {
      const trimmedLine = line.trim();

      if (trimmedLine.match(/^\d+\.\s/)) {
        // This is a numbered list item
        if (!inList) {
          result += '<ol class="outcomes-list">';
          inList = true;
        }
        // Remove the number and dot, keep the content
        const content = trimmedLine.replace(/^\d+\.\s/, "");
        result += `<li>${content}</li>`;
      } else if (trimmedLine !== "") {
        // This is regular text
        if (inList) {
          result += "</ol>";
          inList = false;
        }
        result += `<p>${trimmedLine}</p>`;
      }
    });

    // Close the list if we're still in one
    if (inList) {
      result += "</ol>";
    }

    return result;
  } else {
    // No numbered list, just replace line breaks with paragraphs
    return formattedText
      .split("<br>")
      .filter((line) => line.trim() !== "")
      .map((line) => `<p>${line.trim()}</p>`)
      .join("");
  }
};

const viewLessonMaterials = () => {
  // Open lesson materials in new tab
  window.open(
    `/lesson-materials/${makerCartData.value.makercart_id}?locale=${locale.value}`,
    "_blank"
  );
};

// Mobile-specific methods
const checkScreenSize = () => {
  isMobile.value = window.innerWidth <= 768;
};

const toggleMobileSidebar = () => {
  if (isMobile.value) {
    showMobileSidebar.value = !showMobileSidebar.value;
  }
};

const closeMobileSidebar = () => {
  showMobileSidebar.value = false;
};

const handleMobileSidebarBackdrop = (event) => {
  if (event.target === event.currentTarget && isMobile.value) {
    showMobileSidebar.value = false;
  }
};

const closeModal = () => {
  isVisible.value = false;
  // Restore body scroll immediately
  document.body.style.overflow = "";
  document.documentElement.style.overflow = "";
  // Reset data to prevent showing stale content
  makerCartData.value = {};
  loading.value = false;
  showDetails.value = false;
  currentImageIndex.value = 0;
  selectedPoster.value = false;
  resetTags();
};

const openModal = async (id) => {
  // Set loading state immediately before showing modal
  loading.value = true;
  isVisible.value = true;

  // Prevent body scroll
  document.body.style.overflow = "hidden";
  // Also lock the root element to avoid parent scrollbars
  document.documentElement.style.overflow = "hidden";

  await checkAuth();
  await fetchLimitCounter();

  if (id) {
    console.log("id", id);
    await fetchMakerCartData(id);
  } else {
    // If no ID provided, stop loading
    loading.value = false;
  }
};

// Animation handlers
const onEnter = (el) => {
  el.style.opacity = "0";
  el.style.transform = "scale(0.9)";
};

const onAfterEnter = (el) => {
  el.style.opacity = "1";
  el.style.transform = "scale(1)";
};

const onLeave = (el) => {
  el.style.opacity = "0";
  el.style.transform = "scale(0.9)";
};

const onAfterLeave = () => {
  // Restore body scroll (safety check in case closeModal wasn't called)
  if (document.body.style.overflow === "hidden") {
    document.body.style.overflow = "";
  }
  if (document.documentElement.style.overflow === "hidden") {
    document.documentElement.style.overflow = "";
  }
  emit("close");
};

// Handle ESC key
const handleEscKey = (event) => {
  if (event.key === "Escape" && isVisible.value) {
    closeModal();
  }
};

// Watchers
watch(
  () => props.visible,
  (newVal) => {
    if (newVal && props.makercartId) {
      openModal(props.makercartId);
    } else if (!newVal) {
      closeModal();
    }
  },
  { immediate: true }
);

// Lifecycle
onMounted(() => {
  document.addEventListener("keydown", handleEscKey);
  window.addEventListener("resize", checkScreenSize);
  checkScreenSize(); // Initial check
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  window.removeEventListener("resize", checkScreenSize);
  document.body.style.overflow = "";
});

// Expose methods for parent component
defineExpose({
  openModal,
  closeModal,
});
</script>
<style scoped src="@/assets/css/catalog-makercart.css"></style>
<style scoped>
/* Custom-specific styles */
.makercart-modal-info-section {
  margin-top: 40px;
}

/* Free Resources Tooltip */
.free-resources-tooltip {
  position: absolute;
  top: 55px;
  left: 227px;
  z-index: 10;
}

.tooltip-text {
  font-family: "League Spartan", sans-serif;
  font-size: 12px;
  color: #3b4648;
  cursor: help;
}

/* Mobile Add to Cart Button */
.mobile-add-to-cart-btn {
  background: var(--primary-blue);
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  color: white;
  width: 100%;
  margin: 20px 0;
  transition: background-color 0.2s ease;
}

.mobile-add-to-cart-btn:hover {
  background: #002a4a;
}

/* Mobile Bottom Sheet */
.mobile-sidebar-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 19;
}

.mobile-bottom-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: auto;
  max-height: 80vh;
  background: white;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  z-index: 20;
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.mobile-bottom-sheet.show {
  transform: translateY(0);
}

.mobile-bottom-sheet-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 80vh;
}

.mobile-bottom-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.mobile-bottom-sheet-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-big);
  line-height: 28px;
  color: var(--primary-blue);
  margin: 0;
}

.mobile-bottom-sheet-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.mobile-bottom-sheet-close:hover {
  background: #f3f4f6;
}

.mobile-bottom-sheet-close img {
  width: 24px;
  height: 24px;
}

.mobile-bottom-sheet-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 0;
}

.mobile-form-group {
  margin-bottom: 24px;
}

.mobile-form-label-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.mobile-form-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #40444b;
  flex: 1;
}

.mobile-number-input {
  background: white;
  border: 1px solid #6c737f;
  border-radius: 8px;
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 12px;
  gap: 12px;
  margin-bottom: 12px;
}

.mobile-number-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #00925e;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-number-btn:hover:not(:disabled) {
  background: #f0f9ff;
}

.mobile-number-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mobile-number-input-field {
  flex: 1;
  width: 100%;
  text-align: center;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  color: var(--black-body-text);
  border: none;
  outline: none;
  background: transparent;
  border-right: 1px solid #6c737f;
  border-left: 1px solid #6c737f;
}

.mobile-checkbox-container {
  margin-top: 8px;
  margin-left: 2px;
}

.mobile-radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-radio-option {
  display: flex;
  align-items: center;
  padding: 12px 0;
  cursor: pointer;
  gap: 12px;
}

.mobile-radio-option.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mobile-radio-input {
  display: none;
}

.mobile-radio-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.mobile-radio-input:checked + .mobile-radio-custom {
  border-color: var(--primary-blue);
  background: white;
}

.mobile-radio-input:checked + .mobile-radio-custom::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background: var(--primary-blue);
  border-radius: 50%;
}

.mobile-radio-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  color: var(--primary-blue);
  letter-spacing: 0.08px;
}

.mobile-bottom-sheet-actions {
  padding: 16px 20px;
  padding-bottom: calc(20px + env(safe-area-inset-bottom));
  background: white;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.mobile-action-divider {
  height: 1px;
  background: #e5e7eb;
  margin-bottom: 16px;
}

.mobile-action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Mobile Image Navigation */
.mobile-image-nav {
  position: absolute;
  bottom: 8px;
  right: 16px;
  display: flex;
  gap: 8px;
  z-index: 1;
}

.mobile-nav-btn {
  width: 40px;
  height: 40px;
  background: white;
  border: 1px solid #00925e;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.mobile-nav-btn:hover:not(:disabled) {
  background: #f0f9ff;
  transform: scale(1.05);
}

.mobile-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f5f5f5;
  border-color: #d1d5db;
}

.mobile-nav-btn:disabled svg {
  color: #9ca3af;
}

/* Formatted description styles for Airtable rich text content */
.formatted-description :deep(p) {
  margin: 0 0 0.5em 0;
}

.formatted-description :deep(p:last-child) {
  margin-bottom: 0;
}

.formatted-description :deep(strong) {
  font-weight: 600;
}

.formatted-description :deep(em) {
  font-style: italic;
}

.formatted-description :deep(del) {
  text-decoration: line-through;
}

.formatted-description :deep(ul),
.formatted-description :deep(ol) {
  margin: 0.5em 0;
  padding-left: 1.5em;
}

.formatted-description :deep(li) {
  margin-bottom: 0.25em;
}

.formatted-description :deep(a) {
  color: #00925e;
  text-decoration: underline;
}

.formatted-description :deep(a:hover) {
  color: #007a4e;
}

.formatted-description :deep(br) {
  content: "";
  display: block;
  margin-top: 0.25em;
}
</style>
